<script setup lang="ts">
/**
 * The five password requirements, as chips (Figma `66:47383` / `66:47425`).
 *
 * Deliberately has no error state: while the user types, an unmet rule is
 * neutral grey with a cross, never red (`66:47508`). Red here would shout at
 * someone who is halfway through a perfectly good password.
 *
 * Built on plain markup rather than `FairFilterChip` — that primitive is an
 * interactive filter with hover/press affordances, and suppressing them costs
 * more than the handful of rules below.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { evaluatePassword, PASSWORD_RULES } from '@/features/auth/model/passwordRules'
// Tag leading icons at 16px (`I66:47398;304:7998` / `I66:47444;289:5317`); they
// carry the neutral and positive signal colours from the design.
import ruleUnmetIconUrl from '@/features/auth/ui/icons/rule-unmet.svg'
import ruleMetIconUrl from '@/features/auth/ui/icons/rule-met.svg'

const props = defineProps<{
  password: string
  visible: boolean
}>()

const { t } = useI18n()

const chips = computed(() => {
  const state = evaluatePassword(props.password)
  return PASSWORD_RULES.map((rule) => ({
    id: rule.id,
    label: t(rule.labelKey),
    met: state[rule.id],
  }))
})
</script>

<template>
  <ul
    v-if="visible"
    class="fair-auth-rules"
    aria-live="polite"
  >
    <li
      v-for="chip in chips"
      :key="chip.id"
      :class="['fair-auth-rules__item', chip.met ? 'fair-auth-rules__item--met' : '']"
    >
      <img
        class="fair-auth-rules__icon"
        :src="chip.met ? ruleMetIconUrl : ruleUnmetIconUrl"
        alt=""
      >
      {{ chip.label }}
    </li>
  </ul>
</template>
